import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Grid,
  TableContainer,
  Tooltip,
  Link as MuiLink,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import Table from '@material-ui/core/Table'
import AddOligo from './AddOligo'
import { biTubeConfigContext } from '../../BiTube'
import DeleteDialog from '../../BitubeProductSummary/DeleteDialog'
import { useRouter, fastDuplexOligosRoute } from '@src/routes'
import {
  useSessionFieldsMutation,
  useBiTubePriceCallMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../../messages'
import { ActiveLinkType, BitubeProperties, BiTubeProduct } from '../../enum'
import { priceFormatter } from '@src/routes/Configurators/utils/PriceFormatter'
import ValidationWarnings from '../../../../validations/ValidationWarnings'
import styles from '@src/routes/Configurators/utils/styles'
import OligoNameWarning from '@src/routes/Configurators/validations/oligoNameWarning'
import { getOverhangDisplayValue } from '@src/routes/Configurators/components/OptionalModifications/ModificationResponse'
import { checkValidKey } from '@src/routes/Configurators/utils/util'

const useStyles = makeStyles((theme: Theme) => {
  return {
    cardWrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(3, 5),
      paddingBottom: theme.spacing(0),
      marginBottom: theme.spacing(3),
      borderRadius: 0,
      boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
    },
    subHead: {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: theme.spacing(3, 0),
      paddingLeft: theme.spacing(4),
    },
    dFlex: {
      display: 'flex',
      paddingRight: theme.spacing(4),
    },
    cardContent: {
      padding: theme.spacing(0),
      marginBottom: theme.spacing(-2),
    },
    deleteMargin: {
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5),
    },
    label: {
      fontWeight: 'bold',
    },
    deleteButton: {
      padding: theme.spacing(0),
    },
    deleteAllText: {
      fontSize: theme.typography.pxToRem(16),
    },
    sequenceInfo: {
      fontSize: theme.typography.pxToRem(14),
    },
    alignRight: {
      textAlign: 'right',
    },
    sequenceProperties: {
      width: '46%',
      '& + td': {
        paddingLeft: theme.spacing(2),
      },
    },
    pt20: {
      paddingTop: theme.spacing(5),
    },
    twoCardContainer: {
      padding: theme.spacing(4),
    },
    cardWrapperEven: {
      paddingTop: theme.spacing(2),
    },
    priceDistance: {
      paddingLeft: theme.spacing(12),
      textAlign: 'center',
    },
    properties: {
      paddingLeft: theme.spacing(14),
    },
    addOligo: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    dFlexForZHLang: {
      display: 'flex',
      paddingRight: theme.spacing(8.5),
    },
    tableContainer: {
      maxHeight: 1255,
      marginBottom: theme.spacing(5),
    },
    arrow: {
      color: theme.palette.common.white,
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(18),
      top: theme.spacing(1.5),
      marginLeft: theme.spacing(1.5),
    },
    tooltipRow: {
      display: 'table-row',
    },
    tooltipCell: {
      display: 'table-cell',
      padding: theme.spacing(2),
      maxWidth: theme.spacing(150),
    },
    tooltipCellSeq: {
      maxWidth: theme.spacing(50),
      fontSize: theme.typography.pxToRem(14),
    },
    deleteIcon: {
      fontSize: theme.typography.pxToRem(22),
      color: theme.palette.grey[400],
    },
  }
})

const OrderTable = (props) => {
  const { productDetails, updateError, errorMessage } = props
  const { bitubeState, biTubeDispatch } = React.useContext(biTubeConfigContext)
  const { sessionData, OligoIndex, productInformation } = bitubeState
  // This param is use to set CSS
  const classes = useStyles()
  const intl = useIntl()
  const GlobalCSS = styles()
  const maxItems = BitubeProperties.MaximumOligosAllowed
  // Setting state
  const [delDialogOpen, setDelDialogOpen] = useState(false)
  const [scrollBar, setScrollBar] = useState(false)
  const [delItem, setDelItem] = useState()
  const [delType, setDelType] = useState('')
  const router = useRouter()
  const [isNameWarning, setIsNameWarning] = React.useState(false)
  const sequencePath = `${fastDuplexOligosRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.SequenceEntry}`
  const warningMsg: string[] = []
  warningMsg.push(intl.formatMessage(messages.PC_PRICE_ERROR_EMPTY_PRICE))
  let displayWarning = false
  const [sessionFields] = useSessionFieldsMutation()
  const [biTubePriceCall] = useBiTubePriceCallMutation()
  const sessionDataToSession = async (obj) => {
    await sessionFields({
      variables: { input: obj, productInput: productDetails },
    })
  }
  const oligoNameWarning = (session) => {
    if (session && session.length !== 0) {
      let warning = false
      session.forEach((data) => {
        if (data.name && data.name.length > 18 && warning !== true) {
          warning = true
        }
      })
      setIsNameWarning(warning)
    }
  }

  // React.useEffect
  React.useEffect(() => {
    if (
      sessionData[0] &&
      sessionData[0].additionalComments &&
      sessionData[0].additionalComments === ''
    ) {
      window.scrollTo(0, 0)
    }
    if (bitubeState.sessionData.length > 5) {
      setScrollBar(true)
    }
    oligoNameWarning(sessionData)
    // TODO: need a test before we refactor these dependencies
  }, [bitubeState.sessionData])

  const handleDelete = (item) => {
    setDelDialogOpen(true)
    setDelType('Selected')
    setDelItem(item)
  }

  const setErrorData = (index) => {
    return {
      name: sessionData[index].name,
      sequence: sessionData[index].sequence,
      scale: '',
      purification: sessionData[index].purification,
      fiveMod: '',
      threeMod: sessionData[index].threeMod,
      researcher: '',
      isOverridden: sessionData[index].isOverridden,
      format: sessionData[index].format,
      concentration: sessionData[index].concentration,
      tubes: sessionData[index].tubes,
      customerOverhang: sessionData[index].customerOverhang,
      antisenceName: sessionData[index].antisenceName,
      antisenceSequence: sessionData[index].antisenceSequence,
      customerAntisenseOverHang: sessionData[index].customerAntisenseOverHang,
      antisenceThreeMod: sessionData[index].antisenceThreeMod,
      antisenceFiveMod: sessionData[index].antisenceFiveMod,
      antisenceBaseCount: 0,
      amount: sessionData[index].amount,
      autoGenerateOligo: sessionData[index].autoGenerateOligo,
      senseNameCheck: sessionData[index].senseNameCheck,
    }
  }
  const setInitialState = () => {
    if (sessionData.length !== 0 && sessionData[0].applyToAll) {
      return {
        isOverridden: false,
        name: '',
        sequence: '',
        fiveMod: '',
        purification: sessionData[0].purification,
        baseCount: 0,
        format: sessionData[0].format,
        concentration: sessionData[0].concentration,
        tubes: '1',
        price: '',
        scale: sessionData[0].scale,
        orderItemId: '',
        id: '1',
        comments: '',
        additionalComments: '',
        oligoId: bitubeState.oligoCount + 1,
        countryCode: '',
        autoValidation: 'false',
        productType: '',
        customerOverhang: '',
        antisenceName: '',
        antisenceSequence: '',

        customerAntisenseOverHang: '',
        antisenceBaseCount: 0,
        amount: sessionData[0].amount,
        senseNameCheck: false,
        listError: [{ key: '', value: '' }],
      }
    }
    return {
      isOverridden: false,
      name: '',
      sequence: '',
      fiveMod: '',
      purification: '',
      baseCount: 0,
      format: '',
      concentration: '',
      tubes: '1',
      price: '',
      scale: '',
      orderItemId: '',
      id: '1',
      comments: '',
      additionalComments: '',
      oligoId: bitubeState.oligoCount + 1,
      countryCode: '',
      autoValidation: 'false',
      productType: '',
      customerOverhang: '',
      antisenceName: '',
      antisenceSequence: '',
      autoGenerateOligo: false,
      customerAntisenseOverHang: '',
      antisenceBaseCount: 0,
      amount: '',
      senseNameCheck: false,
      listError: [{ key: '', value: '' }],
    }
  }
  // This method is use to perform delete and delete all operations
  const deleteOligoItem = (item, type) => {
    let isDeleteAll = false
    if (type === 'Selected' || type === 'SelectedViewAll') {
      if (OligoIndex !== 0) {
        const newIndex = OligoIndex - 1
        const array = [...bitubeState.sessionData]
        const index = array.indexOf(item)
        if (index !== -1) {
          array.splice(index, 1)
        }

        biTubeDispatch({
          type: 'UPDATE_OLIGO_INDEX',
          data: newIndex,
        })
        biTubeDispatch({
          type: 'UPDATE_CURRENT_OLIGO_INDEX',
          data: newIndex,
        })
        biTubeDispatch({
          type: 'UPDATE_INPUT',
          data: array,
        })
        biTubeDispatch({
          type: 'UPDATE_ERROR_DATA',
          data: setErrorData(newIndex),
        })
        sessionDataToSession(array)
        oligoNameWarning(array)
      } else {
        isDeleteAll = true
      }
    } else if (type === 'All') {
      isDeleteAll = true
      sessionData[0].applyToAll = false
    }

    if (isDeleteAll) {
      biTubeDispatch({
        type: 'SET_CONTINUE_TO_NEXT_PAGE',
        data: true,
      })
      biTubeDispatch({
        type: 'ON_CONTINUE_BUTTON_PRICECALL',
        data: true,
      })
      biTubeDispatch({
        type: 'UPDATE_INPUT',
        data: [setInitialState()],
      })
      biTubeDispatch({
        type: 'UPDATE_OLIGO_INDEX',
        data: 0,
      })
      biTubeDispatch({
        type: 'UPDATE_CURRENT_OLIGO_INDEX',
        data: 0,
      })
      biTubeDispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...bitubeState.errorData,
          name: '',
          sequence: '',
          scale: '',
          purification: '',
          fiveMod: '',
          threeMod: '',
          researcher: '',
          isOverridden: false,
          format: '',
          concentration: '',
          tubes: '1',
          customerOverhang: '',
          antisenceName: '',
          antisenceSequence: '',
          customerAntisenseOverHang: '',
        },
      })
      biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
      biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
      biTubeDispatch({ type: 'OVERRIDDEN_NUMBER', data: 0 })
      biTubeDispatch({ type: 'CLEAR_SESSION_DATA', data: true })
      router.push(sequencePath)
      sessionDataToSession([])
      if (bitubeState.sessionData.length <= 5) {
        setScrollBar(false)
      }
    }

    biTubeDispatch({
      type: 'UPDATE_PAGE_LOAD',
      data: 1,
    })
    if (
      errorMessage.includes(
        intl.formatMessage(messages.CONF_MAX_ITEMS_ALLOWED, { maxItems })
      )
    ) {
      updateError(false)
    }
  }
  const displaySessionData = (data, index) => {
    if (errorMessage !== undefined && errorMessage !== '') {
      updateError(intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2))
    } else {
      biTubeDispatch({ type: 'CLEAR_SESSION_DATA', data: false })
      router.push(sequencePath)
      biTubeDispatch({
        type: 'UPDATE_ERROR_DATA',
        data: setErrorData(index),
      })
      biTubeDispatch({
        type: 'UPDATE_CURRENT_OLIGO_INDEX',
        data: index,
      })

      biTubeDispatch({
        type: 'UPDATE_PAGE_LOAD',
        data: 1,
      })
    }
  }

  const priceCall = async () => {
    const response: any = await biTubePriceCall({
      variables: { product: productDetails },
    })
    biTubeDispatch({ type: 'CLEAR_SESSION_DATA', data: false })
    if (response.data.biTubePriceCall.length === 0) {
      router.push(sequencePath)
    } else {
      response.data.biTubePriceCall.forEach((item, index) => {
        item.listErrors.forEach((item) => {
          delete item.__typename
        })
        if (item.listErrors.length !== 0) {
          router.push(sequencePath)
        } else {
          if (sessionData.length > index) {
            sessionData[index].price = item.price
            sessionData[index].currencySymbol = item.currencySymbol
            sessionData[index].currency = item.currency
          }
        }
      })
      biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: true })
      biTubeDispatch({ type: 'SET_STEP_NO', data: 1 })
    }
  }

  React.useEffect(() => {
    if (bitubeState.onContinueButtonPriceCall !== true) {
      if (sessionData) {
        priceCall()
      }
    } else {
      biTubeDispatch({
        type: 'ON_CONTINUE_BUTTON_PRICECALL',
        data: false,
      })
    }
    // TODO: need a test before we refactor these dependencies
  }, [])

  const totalDuplexs = () => {
    if (
      productDetails.productType === BiTubeProduct.FastDuplex ||
      productDetails.productType === BiTubeProduct.Duplex ||
      productDetails.productType === BiTubeProduct.mirna
    ) {
      return <FormattedMessage {...messages.CONF_TOTAL_DUPLEX_COUNT} />
    } else {
      return <FormattedMessage {...messages.CONF_TOTAL_OLIGO_COUNT} />
    }
  }
  // This method is use to render data
  const renderData = () => {
    const result: any[] = []
    let viewSenseSequence: string
    let viewAntiSenseSequence: string

    sessionData.forEach((item, index) => {
      if (
        sessionData.length === 1 &&
        item.name !== '' &&
        item.sequence !== '' &&
        (item.price === '' || item.price === null)
      ) {
        displayWarning = true
      }
      if (
        productDetails.productType === BiTubeProduct.Duplex ||
        productDetails.productType === BiTubeProduct.mirna
      ) {
        //For Display SenseSequence in pop-up
        const fiveMod =
          item.fiveMod?.replace(/[[\]]/g, '') !== BitubeProperties.NoneMod
            ? item.fiveMod
            : ''
        const threeMod =
          item.threeMod?.replace(/[[\]]/g, '') !== BitubeProperties.NoneMod
            ? item.threeMod
            : ''
        const overHang =
          item.customerOverhang !== BitubeProperties.None
            ? getOverhangDisplayValue(
                bitubeState.specificationResponse,
                item.customerOverhang
              )
            : ''
        viewSenseSequence = `${fiveMod}${item.sequence}${overHang}${threeMod}`
        //Display AntiSense Sequence in Pop-up
        const antisenceFiveMod =
          item.antisenceFiveMod?.replace(/[[\]]/g, '') !==
          BitubeProperties.NoneMod
            ? item.antisenceFiveMod
            : ''
        const antisenceThreeMod =
          item.antisenceThreeMod?.replace(/[[\]]/g, '') !==
          BitubeProperties.NoneMod
            ? item.antisenceThreeMod
            : ''
        const antiOverHang =
          item.customerAntisenseOverHang !== BitubeProperties.None
            ? getOverhangDisplayValue(
                bitubeState.specificationResponse,
                item.customerAntisenseOverHang
              )
            : ''
        if (productDetails.productType === BiTubeProduct.mirna) {
          viewAntiSenseSequence = `${item.antisenceSequence}${antisenceThreeMod}`
        } else {
          viewAntiSenseSequence = `${antisenceFiveMod}${item.antisenceSequence}${antiOverHang}${antisenceThreeMod}`
        }
      } else if (productDetails.productType === BiTubeProduct.LightCycler) {
        const fiveMod =
          item.fiveMod &&
          item.fiveMod?.replace(/[[\]]/g, '') !== BitubeProperties.NoneMod
            ? item.fiveMod
            : ''
        const threeMod =
          item.threeMod &&
          item.threeMod?.replace(/[[\]]/g, '') !== BitubeProperties.NoneMod
            ? item.threeMod
            : ''
        const antisenceThreeMod =
          item.antisenceThreeMod &&
          item.antisenceThreeMod?.replace(/[[\]]/g, '') !==
            BitubeProperties.NoneMod
            ? item.antisenceThreeMod
            : ''
        const antisenceFiveMod =
          item.antisenceFiveMod &&
          item.antisenceFiveMod?.replace(/[[\]]/g, '') !==
            BitubeProperties.NoneMod
            ? item.antisenceFiveMod
            : ''
        viewSenseSequence = `${fiveMod}${item.sequence}${threeMod}`

        viewAntiSenseSequence = `${antisenceFiveMod}${item.antisenceSequence}${antisenceThreeMod}`
      } else {
        viewSenseSequence = `${item.sequence}${
          item.customerOverhang !== BitubeProperties.None
            ? getOverhangDisplayValue(
                bitubeState.specificationResponse,
                item.customerOverhang
              )
            : ''
        }`

        viewAntiSenseSequence = `${item.antisenceSequence}${
          item.customerAntisenseOverHang !== BitubeProperties.None
            ? getOverhangDisplayValue(
                bitubeState.specificationResponse,
                item.customerAntisenseOverHang
              )
            : ''
        }`
      }

      result.push(
        <div className={classes.twoCardContainer} key={index}>
          <Card className={classes.cardWrapper}>
            <CardContent className={classes.cardContent}>
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="flex-start"
              >
                <Grid item sm={3}>
                  <div className={GlobalCSS.OligoName}>
                    <h4>
                      <MuiLink onClick={() => displaySessionData(item, index)}>
                        <strong data-scrub>
                          {productDetails.productType === BiTubeProduct.mirna
                            ? item.antisenceName
                            : item.name}
                        </strong>
                      </MuiLink>
                    </h4>
                  </div>
                  <div className={classes.sequenceProperties}>
                    <FormattedMessage {...messages.CONF_TOTAL_BASES} />
                    {productDetails.productType === BiTubeProduct.mirna
                      ? item.antisenceBaseCount
                      : item.baseCount}
                  </div>
                </Grid>
                <Grid item sm={3} className={GlobalCSS.seqWordWrapwithDot}>
                  <div>
                    <Tooltip
                      title={
                        <>
                          <div className={GlobalCSS.seqWordWrap}>
                            {productDetails.productType !==
                            BiTubeProduct.LightCycler ? (
                              productDetails.productType ===
                              BiTubeProduct.mirna ? (
                                <FormattedMessage
                                  {...messages.CONF_ANTISENSE_SEQUENCE}
                                />
                              ) : (
                                <FormattedMessage
                                  {...messages.CONF_SENSE_SEQUENCE}
                                />
                              )
                            ) : (
                              <FormattedMessage
                                {...messages.CONF_DONOR_lightcycler_sequence}
                              />
                            )}
                          </div>
                          <div className={GlobalCSS.sequenceSpacing} data-scrub>
                            {productDetails.productType === BiTubeProduct.mirna
                              ? viewAntiSenseSequence
                                ? viewAntiSenseSequence
                                : '--'
                              : viewSenseSequence
                                ? viewSenseSequence
                                : '--'}
                          </div>
                        </>
                      }
                      interactive
                      classes={{
                        tooltip: GlobalCSS.InfoPopup,
                        arrow: classes.arrow,
                      }}
                      placement="bottom"
                    >
                      <MuiLink
                        color="primary"
                        className={GlobalCSS.seqWordWrapwithDot}
                        data-scrub
                      >
                        {productDetails.productType === BiTubeProduct.mirna
                          ? viewAntiSenseSequence
                            ? viewAntiSenseSequence
                            : '--'
                          : viewSenseSequence
                            ? viewSenseSequence
                            : '--'}
                      </MuiLink>
                    </Tooltip>
                  </div>
                </Grid>
                <Grid item sm={3}>
                  <div className={classes.properties}>
                    <Table>
                      <tbody>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_AMOUNT} />
                            </strong>
                          </td>
                          <td>
                            {item.amount?.length !== 0 ? item.amount : '--'}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_PURIFICATION_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.purification?.length ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`_PC_SPEC_${item.purification}`],
                                  `_PC_SPEC_${item.purification}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_FORMAT_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.format?.length ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`${item.format}`],
                                  `${item.format}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_TUBES} />
                            </strong>
                          </td>
                          <td>
                            <label>{item.tubes}</label>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Grid>
                <Grid item sm={2}>
                  <div
                    className={clsx(
                      classes.sequenceInfo,
                      classes.priceDistance
                    )}
                  >
                    {item.price
                      ? item.price === BitubeProperties.Quote
                        ? BitubeProperties.QuoteItem
                        : priceFormatter(
                            item.price,
                            productInformation.currencyFormat,
                            productInformation.thousandSeparator,
                            productInformation.thousandSpacing,
                            productInformation.decimalSeparator,
                            productInformation.prefixCurrecny,
                            item.currencySymbol
                          )
                      : '--'}
                  </div>
                </Grid>
                <Grid item sm={1} className={classes.alignRight}>
                  <div>
                    <IconButton
                      aria-label="delete"
                      className={classes.deleteButton}
                      onClick={() => {
                        handleDelete(item)
                      }}
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  </div>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card className={classes.cardWrapper} key={index}>
            <CardContent className={classes.cardContent}>
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="flex-start"
              >
                <Grid item sm={3}>
                  <div className={GlobalCSS.OligoName}>
                    <h4>
                      <MuiLink onClick={() => displaySessionData(item, index)}>
                        <strong>
                          {productDetails.productType === BiTubeProduct.mirna
                            ? item.name
                            : item.antisenceName}
                        </strong>
                      </MuiLink>
                    </h4>
                  </div>
                  <div className={classes.sequenceProperties}>
                    <FormattedMessage {...messages.CONF_TOTAL_BASES} />
                    {productDetails.productType === BiTubeProduct.mirna
                      ? item.baseCount
                      : item.antisenceBaseCount}
                  </div>
                </Grid>
                <Grid item sm={3} className={GlobalCSS.seqWordWrapwithDot}>
                  <div>
                    <Tooltip
                      title={
                        <>
                          <div className={GlobalCSS.seqWordWrap}>
                            {productDetails.productType !==
                            BiTubeProduct.LightCycler ? (
                              productDetails.productType ===
                              BiTubeProduct.mirna ? (
                                <FormattedMessage
                                  {...messages.CONF_SENSE_SEQUENCE}
                                />
                              ) : (
                                <FormattedMessage
                                  {...messages.CONF_ANTISENSE_SEQUENCE}
                                />
                              )
                            ) : (
                              <FormattedMessage
                                {...messages.CONF_ACCEPTOR_lightcycler_sequence}
                              />
                            )}
                          </div>
                          <div className={GlobalCSS.sequenceSpacing}>
                            {productDetails.productType === BiTubeProduct.mirna
                              ? viewSenseSequence
                                ? viewSenseSequence
                                : '--'
                              : viewAntiSenseSequence
                                ? viewAntiSenseSequence
                                : '--'}
                          </div>
                        </>
                      }
                      interactive
                      classes={{
                        tooltip: GlobalCSS.InfoPopup,
                        arrow: classes.arrow,
                      }}
                      placement="bottom"
                    >
                      <MuiLink
                        color="primary"
                        className={GlobalCSS.seqWordWrapwithDot}
                      >
                        {productDetails.productType === BiTubeProduct.mirna
                          ? viewSenseSequence
                            ? viewSenseSequence
                            : '--'
                          : viewAntiSenseSequence
                            ? viewAntiSenseSequence
                            : '--'}
                      </MuiLink>
                    </Tooltip>
                  </div>
                </Grid>
                <Grid item sm={3}>
                  <div className={classes.properties}>
                    <Table>
                      <tbody>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_AMOUNT} />
                            </strong>
                          </td>
                          <td>
                            {item.amount?.length !== 0 ? item.amount : '--'}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_PURIFICATION_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.purification?.length ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`_PC_SPEC_${item.purification}`],
                                  `_PC_SPEC_${item.purification}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_FORMAT_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.format?.length ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`${item.format}`],
                                  `${item.format}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_TUBES} />
                            </strong>
                          </td>
                          <td>
                            <label>{item.tubes}</label>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Grid>
                <Grid item sm={2}>
                  <div
                    className={clsx(
                      classes.sequenceInfo,
                      classes.priceDistance
                    )}
                  ></div>
                </Grid>
                <Grid
                  item
                  sm={1}
                  className={clsx(classes.alignRight, classes.pt20)}
                ></Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
      )
    })
    return result
  }

  return (
    <>
      <div className={classes.header}>
        <h3 className={classes.subHead}>
          <strong>
            <FormattedMessage {...messages.CONF_CONFIRM_ORDER} />
          </strong>
        </h3>

        <div className={scrollBar ? classes.dFlexForZHLang : classes.dFlex}>
          <h4>
            <strong
              className={clsx(
                GlobalCSS.globalBlackFontColor,
                classes.deleteAllText
              )}
            >
              <FormattedMessage {...messages.CONF_DELETE_ALL_BUTTON} />
            </strong>
          </h4>
          <span className={classes.deleteMargin}>
            <IconButton
              aria-label="delete"
              onClick={() => {
                setDelDialogOpen(true)
                setDelType('All')
                setDelItem(delItem)
              }}
              className={classes.deleteButton}
              id="step2-delete-all-button"
              data-testid="step2-delete-all-button"
            >
              <DeleteIcon className={classes.deleteIcon} />
            </IconButton>
          </span>
        </div>
      </div>
      {isNameWarning ? (
        <Grid className={GlobalCSS.marginLeft4}>
          <OligoNameWarning
            warning={intl.formatMessage(messages.CONF_OLIGO_NAME_WARNING)}
          />
        </Grid>
      ) : (
        ''
      )}
      <div className={GlobalCSS.duplexCount}>
        {totalDuplexs()} {sessionData.length}
      </div>
      <TableContainer className={scrollBar ? classes.tableContainer : ''}>
        {renderData()}
      </TableContainer>
      <div className={classes.addOligo}>
        <AddOligo
          productType={productDetails.productType}
          updateError={updateError}
          errorMessage={errorMessage}
        />
      </div>
      <DeleteDialog
        dialogOpen={delDialogOpen}
        setDialogOpen={setDelDialogOpen}
        selectedItem={delItem}
        type={delType}
        onDelete={deleteOligoItem}
      />
      {bitubeState.circuitBreak || displayWarning ? (
        <div className={classes.addOligo}>
          <ValidationWarnings warnings={warningMsg} />
        </div>
      ) : (
        ''
      )}
    </>
  )
}

export default OrderTable
